import React, { useEffect, useState } from "react";
import styles from "./PostMonitoring.module.scss";
import CustomHeader from "components/common/CustomHeader/CustomHeader";
import Table from "components/common/Table/Table";
import FlagSVG from "components/SVGComponents/FlagSVG";
import Tooltip from "components/common/Tooltip/Tooltip";
import { downloadFile, formatDateToReadable } from "utils/constants";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import SelectCustom from "components/common/Select/Select";
import ViewSVG from "components/SVGComponents/ViewSVG";
import {
  exportPosts,
  getAllPosts,
  handlePostFlaggedStatus,
  handlePostStatus,
} from "reducer/admin/postMonitoringSlice";
import {
  flaggedStatusOptions,
  getFlaggedStatusOption,
  getStatusOption,
  selectStyles,
  statusOptions,
} from "../constants";
import ReportModal from "components/UI/ReportModal/ReportModal";
import ReportReason from "../SharedComponents/ReportReason/ReportReason";
import { debounce } from "components/admin/constants";
import FilterModal from "./FilterModal";

const PostMonitoring = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [rowsPerPageValue, setRowsPerPageValue] = useState(10);
  const [selectedRow, setSelectedRow] = useState();
  const [flaggedModal, setFlaggedModal] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reasonModal, setReasonModal] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { control } = useForm();
  const dispatch = useDispatch();

  const { posts, totalCount, isPostsLoading, isExporting, filters } =
    useSelector((state) => state.posts);

  const handleSearch = debounce((value) => {
    setSearchValue(value);
    dispatch(getAllPosts({ pageSelected, search: value, filters }));
  }, 500);

  const handleToggleStatus = async (row) => {
    try {
      await dispatch(
        handlePostStatus({
          postId: row.postId,
          isActive: !row?.isActive,
          message: reportText,
        })
      ).unwrap();
      setFlaggedModal(false);
      setReportText("");
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleFlaggedModal = async (value, row) => {
    if (value === "inActive") {
      setFlaggedModal(true);
    } else {
      handleToggleStatus(row);
    }
  };

  const handleExport = async () => {
    const res = await dispatch(exportPosts()).unwrap();
    downloadFile(res);
  };

  const updateFlaggedStatus = async (payload) => {
    try {
      await dispatch(handlePostFlaggedStatus(payload)).unwrap();
      setFlaggedModal(false);
    } catch (error) {
      console.log("error", error);
    }
  };

  const columns = [
    {
      name: "User Name",
      selector: (row) => (
        <Tooltip
          data={`${row?.firstName} ${
            row?.userType === "CREATOR" ? row?.lastName : ""
          }`}
        />
      ),
      width: "15%",
    },
    {
      name: "User Type",
      selector: (row) => row?.userType || "-",
      width: "12%",
    },
    {
      name: "Date & Time",
      selector: (row) =>
        <Tooltip data={formatDateToReadable(row?.createdAt)} /> || "-",
      width: "15%",
    },
    {
      name: "Deleted By",
      selector: (row) => row?.flaggedBy || "-",
      width: "13%",
    },
    {
      name: "Status",
      selector: (row) => (
        <Controller
          name={`status-${row.userAliasId}`}
          control={control}
          defaultValue={getStatusOption(row.isActive)}
          render={({ field }) => (
            <SelectCustom
              {...field}
              onChange={(selectedOption) => {
                field.onChange(selectedOption);
                handleFlaggedModal(selectedOption?.value, row);
                setSelectedRow(row);
              }}
              value={getStatusOption(row.isActive)}
              options={statusOptions}
              styles={selectStyles(!row.isActive)}
            />
          )}
        />
      ),
      width: "15%",
    },
    {
      name: "Flag Status",
      selector: (row) => (
        <Controller
          name={`flaggedStatus-${row.userAliasId}`}
          control={control}
          defaultValue={getFlaggedStatusOption(row.flaggedStatus)}
          render={({ field }) => (
            <SelectCustom
              {...field}
              isDisabled={!row.isReported}
              onChange={(selectedOption) => {
                field.onChange(selectedOption);
                updateFlaggedStatus({
                  postId: row.postId,
                  flaggedStatus: selectedOption?.value,
                });
              }}
              value={getFlaggedStatusOption(row.flaggedStatus)}
              options={flaggedStatusOptions}
              styles={selectStyles(row.isFlagged)}
            />
          )}
        />
      ),
      width: "15%",
    },
    {
      name: "Action",
      selector: (row) => (
        <div className={styles.actions}>
          <span>
            <FlagSVG
              height={20}
              width={18}
              fill={row?.isReported ? "#FF6A6A" : "none"}
              color={row?.isReported ? "#FF6A6A" : "#CCCCCC80"}
            />
          </span>
          <span>
            {row?.flaggedReason && (
              <span onClick={() => setReasonModal(row.flaggedReason)}>
                <ViewSVG />
              </span>
              // <Tooltip
              //   data={row.flaggedReason}
              //   tooltipBackground="#FF3434"
              //   force={true}
              // >
              //   <ViewSVG />
              // </Tooltip>
            )}
          </span>
        </div>
      ),
      width: "15%",
    },
  ];

  useEffect(() => {
    dispatch(getAllPosts({ pageSelected, filters }));
  }, [pageSelected, dispatch]);

  return (
    <>
      <div className={styles.main}>
        <CustomHeader
          handleSearch={handleSearch}
          isExporting={isExporting}
          handleExport={handleExport}
          openModal={openModal}
          setOpenModal={setOpenModal}
          
        />
        {openModal && (
          <FilterModal
            onClose={() => setOpenModal(false)}
            searchTerm={searchValue}
          />
        )}

        <Table
          columns={columns}
          data={posts || []}
          className={styles.candidatesTable}
          progressPending={isPostsLoading}
          paginationProps={{
            isPagination: true,
            tableName: "Post Monitoring",
            pageSelected,
            totalCount: totalCount,
            rowsPerPageValue,
            setRowsPerPageValue,
            setPageSelected,
          }}
        />

        {flaggedModal && (
          <ReportModal
            reportModal={flaggedModal}
            setReportModal={setFlaggedModal}
            coverImage={false}
            type="post"
            handleSubmit={() => handleToggleStatus(selectedRow)}
            setReport={setReportText}
          />
        )}
      </div>
      {reasonModal && (
        <ReportReason reason={reasonModal} setReasonModal={setReasonModal} />
      )}
    </>
  );
};

export default PostMonitoring;
